// src/hooks/messaging/useVariationMessaging.js

import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useState } from 'react';
import {
  sendMessage,
  fetchConversation,
  setActiveThread,
  selectConversations,
  selectCurrentConversation,
  selectMessages,
  selectMessagesLoading
} from '../../slices/messageSlice';
import { VARIATION_MESSAGE_TYPES } from '../../constants/messages/variationMessages';
import { selectCurrentUser } from '../../slices/userSlice';

/**
 * Custom hook for managing variation-related messaging
 * Provides unified interface for Voice Actor, Book Creator, and System messages
 */
const useVariationMessaging = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const conversations = useSelector(selectConversations);
  const currentConversation = useSelector(selectCurrentConversation);
  const loading = useSelector(selectMessagesLoading);
  const [error, setError] = useState(null);

  /**
   * Get conversation ID between two users
   */
  const getConversationId = useCallback((userId1, userId2) => {
    // Sort user IDs to ensure consistent conversation ID
    const sortedIds = [userId1, userId2].sort();
    return `conv-${sortedIds[0]}-${sortedIds[1]}`;
  }, []);

  /**
   * Post a specific version to Book Creator
   * Used when Voice Actor wants to notify Book Creator about a specific version
   */
  const postVersion = useCallback(async ({
    recipientId,
    variationId,
    versionId,
    versionNumber,
    variationName,
    characterName,
    bookTitle,
    messageContent = '',
    metadata = {}
  }) => {
    try {
      setError(null);

      const messageData = {
        recipient: { userId: recipientId },
        sender: { userId: currentUser?.userId || currentUser?.data?.userId },
        messageType: VARIATION_MESSAGE_TYPES.VARIATION_RECORDING_POSTED,
        messageContent: messageContent ||
          `I've posted Version ${versionNumber} of "${variationName}" for your review.`,

        // Thread by variation for conversation continuity
        threadId: `variation-${variationId}`,
        conversationId: getConversationId(
          currentUser?.userId || currentUser?.data?.userId,
          recipientId
        ),

        // Variation reference - backend will look up full data
        metadata: {
          variationId,
          versionId,
          versionNumber,
          variationName,
          characterName,
          bookTitle,
          action: 'POST_VERSION',
          timestamp: new Date().toISOString(),
          ...metadata
        }
      };

      console.log('[useVariationMessaging] Posting version:', messageData);
      const result = await dispatch(sendMessage(messageData)).unwrap();

      return { success: true, message: result };
    } catch (error) {
      console.error('[useVariationMessaging] Error posting version:', error);
      setError(error.message || 'Failed to post version');
      return { success: false, error: error.message };
    }
  }, [currentUser, dispatch, getConversationId]);

  /**
   * Submit entire variation for review
   */
  const submitVariation = useCallback(async ({
    recipientId,
    variationId,
    variationName,
    characterName,
    bookTitle,
    messageContent = '',
    metadata = {}
  }) => {
    try {
      setError(null);

      const messageData = {
        recipient: { userId: recipientId },
        sender: { userId: currentUser?.userId || currentUser?.data?.userId },
        messageType: VARIATION_MESSAGE_TYPES.VARIATION_RECORDING_POSTED,
        messageContent: messageContent ||
          `I've submitted the variation "${variationName}" for "${characterName}" in "${bookTitle}".`,

        threadId: `variation-${variationId}`,
        conversationId: getConversationId(
          currentUser?.userId || currentUser?.data?.userId,
          recipientId
        ),

        metadata: {
          variationId,
          variationName,
          characterName,
          bookTitle,
          action: 'SUBMIT_VARIATION',
          timestamp: new Date().toISOString(),
          ...metadata
        }
      };

      const result = await dispatch(sendMessage(messageData)).unwrap();
      return { success: true, message: result };
    } catch (error) {
      console.error('[useVariationMessaging] Error submitting variation:', error);
      setError(error.message || 'Failed to submit variation');
      return { success: false, error: error.message };
    }
  }, [currentUser, dispatch, getConversationId]);

  /**
   * Request revision from Voice Actor (Book Creator action)
   */
  const requestRevision = useCallback(async ({
    voiceActorId,
    variationId,
    variationName,
    issues,
    requestedChanges,
    deadline,
    metadata = {}
  }) => {
    try {
      setError(null);

      const messageData = {
        recipient: { userId: voiceActorId },
        sender: { userId: currentUser?.userId || currentUser?.data?.userId },
        messageType: VARIATION_MESSAGE_TYPES.VARIATION_RETAKE_REQUEST,
        messageContent: `Revision requested for "${variationName}". ${requestedChanges || ''}`,

        threadId: `variation-${variationId}`,
        conversationId: getConversationId(
          currentUser?.userId || currentUser?.data?.userId,
          voiceActorId
        ),

        metadata: {
          variationId,
          variationName,
          revisionRequest: {
            issues,
            requestedChanges,
            deadline,
            requestedAt: new Date().toISOString()
          },
          action: 'REQUEST_REVISION',
          ...metadata
        }
      };

      const result = await dispatch(sendMessage(messageData)).unwrap();
      return { success: true, message: result };
    } catch (error) {
      console.error('[useVariationMessaging] Error requesting revision:', error);
      setError(error.message || 'Failed to request revision');
      return { success: false, error: error.message };
    }
  }, [currentUser, dispatch, getConversationId]);

  /**
   * Approve variation (Book Creator action)
   */
  const approveVariation = useCallback(async ({
    voiceActorId,
    variationId,
    variationName,
    feedback,
    qualityScore,
    metadata = {}
  }) => {
    try {
      setError(null);

      const messageData = {
        recipient: { userId: voiceActorId },
        sender: { userId: currentUser?.userId || currentUser?.data?.userId },
        messageType: VARIATION_MESSAGE_TYPES.VARIATION_REVIEW_RESPONSE,
        messageContent: feedback || `Great work on "${variationName}"! Approved.`,

        threadId: `variation-${variationId}`,
        conversationId: getConversationId(
          currentUser?.userId || currentUser?.data?.userId,
          voiceActorId
        ),

        metadata: {
          variationId,
          variationName,
          approvalData: {
            feedback,
            qualityScore,
            approvedAt: new Date().toISOString()
          },
          action: 'APPROVE_VARIATION',
          ...metadata
        }
      };

      const result = await dispatch(sendMessage(messageData)).unwrap();
      return { success: true, message: result };
    } catch (error) {
      console.error('[useVariationMessaging] Error approving variation:', error);
      setError(error.message || 'Failed to approve variation');
      return { success: false, error: error.message };
    }
  }, [currentUser, dispatch, getConversationId]);

  /**
   * Get all messages for a specific variation thread
   */
  const getVariationThread = useCallback(async (variationId, otherUserId) => {
    try {
      setError(null);

      return await dispatch(fetchConversation({
        userId: currentUser?.userId || currentUser?.data?.userId,
        otherUserId,
        threadId: `variation-${variationId}`,
        limit: 100
      })).unwrap();
    } catch (error) {
      console.error('[useVariationMessaging] Error fetching variation thread:', error);
      setError(error.message || 'Failed to fetch conversation');
      return null;
    }
  }, [currentUser, dispatch]);

  /**
   * Set active thread for a variation
   */
  const focusVariationThread = useCallback((variationId) => {
    if (currentConversation?.conversationId) {
      dispatch(setActiveThread({
        conversationId: currentConversation.conversationId,
        threadId: `variation-${variationId}`
      }));
    }
  }, [currentConversation, dispatch]);

  /**
   * Get messages for current variation from Redux state
   */
  const getVariationMessages = useCallback((variationId) => {
    if (!currentConversation?.conversationId) return [];

    const allMessages = selectMessages(currentConversation.conversationId)({
      user: { messages: { messages: { [currentConversation.conversationId]: [] } } }
    });

    return allMessages.filter(
      msg => msg.threadId === `variation-${variationId}` ||
        msg.metadata?.variationId === variationId
    );
  }, [currentConversation]);

  /**
   * Generate contextual message content
   */
  const generateMessageContent = useCallback((type, variationName, additionalInfo = {}) => {
    const templates = {
      submission: `I've completed the recording for "${variationName}". Please review at your convenience.`,
      post_version: `Version ${additionalInfo.versionNumber || 'new'} of "${variationName}" is ready for your review.`,
      revision: `I've addressed your feedback on "${variationName}". The updated version is ready.`,
      approval: `Excellent work on "${variationName}"! Approved for production.`,
      feedback: `Please see my notes on "${variationName}" for suggested improvements.`,
      milestone: `Milestone reached: ${additionalInfo.milestone || 'Progress update'} for "${variationName}".`
    };

    return templates[type] || `Update regarding variation: "${variationName}"`;
  }, []);

  /**
   * Clear any errors
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    // Voice Actor actions
    postVersion,
    submitVariation,

    // Book Creator actions
    requestRevision,
    approveVariation,

    // Thread management
    getVariationThread,
    focusVariationThread,
    getVariationMessages,

    // Utilities
    generateMessageContent,
    clearError,

    // State
    isLoading: loading.sending,
    error,
    conversations,
    currentConversation
  };
};

export default useVariationMessaging;
